import { useMemo } from 'react';
import { isSquareTradeAddOn } from '../../../common/utils/addOnUtils.js';
import { userHasEmptyOrSMEPriceGroup } from '../../../common/utils/employeeUtils.js';
import { VAT_MULTIPLIER, getVatAsString } from '../../../common/utils/taxUtils.js';
import { addToShoppingBasketFromContainer } from '../../../components/ShoppingBasket/shoppingBasketFunctions.js';
import {
  AuthenticatedUserRole,
  Catalog,
  CommercialProductSubType,
  CommercialProductType,
  EppCategory,
  EppSolutionResponse,
  OnlineModelCategory,
} from '../../../generated/api/models.js';
import { getImageUrl, pictureStorageBaseUrl } from '../../Picture/contentfulImageProxy.js';
import { checkCpEligibilityForEmployee, isEmployeeUser } from './productDetailsEmployeeUtils.js';
import type {
  AddOn,
  AddOnAssociation,
  CommercialProduct,
  CompanyInfoResponse,
  Offer,
  OfferEppCategory,
  OnlineModel,
  Price,
} from '../../../generated/api/models.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { ProductDetailsPropsComponentBottomRightSide, SelectedAddOnBundle } from '../ProductDetails.js';
import type { ProductDetailsAddOnStateProperties } from '../partials/ProductDetailsAddOn.js';
import type { PictureData } from '../partials/ProductDetailsPicture.js';

const DAMAGE_INSURANCE_ADDON_GROUP = 'Vahinkopalvelu';

const fromOffers = (offers: Offer[]): PictureData[] => {
  // NOTE: this supports old images starting with static.elisa and new with full path. First can be removed after product variant migration
  const pictures = new Map<string, PictureData>();
  offers.forEach(({ images = [], offerName, offerId }) => {
    images.forEach(rawImage => {
      const image = rawImage.trim();
      const src =
        image.startsWith('/test-assets/') || image.startsWith(pictureStorageBaseUrl)
          ? image
          : pictureStorageBaseUrl + image.split('/image/').pop();
      if (src) {
        pictures.set(image, {
          // the last part of the image url, the filename
          id: src.substring(src.lastIndexOf('/') + 1),
          src: src,
          name: offerName,
          offerIds: [...(pictures.get(image)?.offerIds || []), offerId],
        });
      }
    });
  });
  return Array.from(pictures.values());
};

const fromOnlineModel = (onlineModel: OnlineModel): PictureData[] => {
  if (onlineModel.images?.length) {
    return onlineModel.images.map((url, index) => {
      return {
        id: index.toString(),
        name: `${onlineModel.manufacturer} ${onlineModel.onlineModelName}`,
        src: url.startsWith(pictureStorageBaseUrl) ? url : pictureStorageBaseUrl + url.split('/image/').pop(),
        offerIds: onlineModel.offers.map(o => o.offerId),
      };
    });
  }
  if (onlineModel.listingImage) {
    return [
      {
        id: onlineModel.onlineModelCode,
        name: `${onlineModel.manufacturer} ${onlineModel.onlineModelName}`,
        src: onlineModel.listingImage.startsWith(pictureStorageBaseUrl)
          ? onlineModel.listingImage
          : pictureStorageBaseUrl + onlineModel.listingImage.split('/image/').pop(),
        offerIds: onlineModel.offers.map(o => o.offerId),
      },
    ];
  }
  return [];
};

export const valueOrZero = (input: number | undefined): number => input || 0;

const getPreferredPrice = (price?: { effectivePrice?: Price; listPrice?: Price }): Price | undefined => {
  return price?.effectivePrice ?? price?.listPrice;
};

const normalizeCommercialProductCharges = (commercialProduct: CommercialProduct): CommercialProduct => {
  const preferredPrice = getPreferredPrice(commercialProduct.price);
  if (!preferredPrice) {
    return commercialProduct;
  }

  return {
    ...commercialProduct,
    monthlyRecurringCharge: preferredPrice.monthlyRecurringCharge ?? commercialProduct.monthlyRecurringCharge ?? 0,
    oneTimeCharge: preferredPrice.oneTimeCharge ?? commercialProduct.oneTimeCharge ?? 0,
    payments: preferredPrice.payments ?? commercialProduct.payments,
  };
};

const normalizeAddOnCharges = (addOn: AddOn): AddOn => {
  const preferredPrice = getPreferredPrice(addOn.price);
  if (!preferredPrice) {
    return addOn;
  }

  return {
    ...addOn,
    addOnMonthlyRecurringCharge: preferredPrice.monthlyRecurringCharge ?? addOn.addOnMonthlyRecurringCharge ?? 0,
    addOnOneTimeCharge: preferredPrice.oneTimeCharge ?? addOn.addOnOneTimeCharge ?? 0,
  };
};

/**
 * Primarily assemble pictures from Offers
 * Secondly do it from OnlineModel
 * @param offers Offer[]
 * @param onlineModel OnlineModel
 */
export const assemblePictures = (offers: Offer[], onlineModel: OnlineModel): PictureData[] => {
  return offers.length ? fromOffers(offers) : fromOnlineModel(onlineModel);
};

/**
 * Have those offers with active true
 * Sort with the best availability first and the worst availability last
 * @param offers Offer[]
 */
export const filterAndSortOffersByAvailability = (offers: Offer[]) => {
  return offers.length
    ? offers
        .filter(o => o.active)
        .sort((a, b) => {
          // Sort to last if data is not available at all
          if (!a.stockAvailabilityRange) {
            return 1;
          }

          const l1 = valueOrZero(a.stockAvailabilityRange?.lower);
          const l2 = valueOrZero(b.stockAvailabilityRange?.lower);

          if (l1 < l2) {
            return 1;
          }

          if (l1 > l2) {
            return -1;
          }

          return 0;
        })
    : [];
};

export const filterAndSortOffersByName = (offers: Offer[]) => {
  return offers.length
    ? offers.sort((a, b) => a.offerName.localeCompare(b.offerName, undefined, { numeric: true }))
    : [];
};

const isDevice = (commercialProduct: CommercialProduct) =>
  commercialProduct.productType === CommercialProductType.HANDHELD_DEVICE;

export const isCommercialProductPaymentAllowed = (
  cp: CommercialProduct,
  segmentPricingGroup?: string,
  eppSolution?: EppSolutionResponse
) =>
  userHasEmptyOrSMEPriceGroup(segmentPricingGroup) ||
  eppSolution?.allowDevicesWithRecurringCharges ||
  cp.payments === 1 ||
  cp.productSubType === CommercialProductSubType.EPP_DEVICE ||
  !isDevice(cp);

export const isEppProduct = (commercialProduct: CommercialProduct) =>
  commercialProduct.productSubType === CommercialProductSubType.EPP_DEVICE;

const isAdminUser = (user?: AuthenticatedUser): boolean =>
  user?.userRole === AuthenticatedUserRole.ADMIN || user?.userRole === AuthenticatedUserRole.KEY_USER;

const isEppSolutionActive = (companyInfo?: CompanyInfoResponse): boolean =>
  companyInfo?.eppSolution?.eppSolutionStatus === EppSolutionResponse.EppSolutionStatusEnum.ACTIVE;

const isRecurringChargesAllowed = (companyInfo: CompanyInfoResponse): boolean =>
  companyInfo.eppSolution?.allowDevicesWithRecurringCharges || false;

const isOneTimeChargeAllowed = (companyInfo: CompanyInfoResponse): boolean =>
  companyInfo.eppSolution?.allowDevicesWithOneTimeCharges || false;

const isEligibleEppAdmin = (user?: AuthenticatedUser, companyInfo?: CompanyInfoResponse): boolean =>
  isAdminUser(user) && isEppSolutionActive(companyInfo);

const isOneTimePayment = (cp?: CommercialProduct, addOn?: AddOn) => cp?.payments === 1 || addOn?.addOnOneTimeCharge;

const calculateMonthlyChargeWithCorporateShare = (commercialProduct: CommercialProduct, corporateShare: number) =>
  valueOrZero(commercialProduct.monthlyRecurringCharge) - corporateShare;

// In case of EPP prices, subtract corporate share from the prices shown to employee (show 0 if corporate share is greater than device price, show full price if corporate share is undefined)
const calculateEmployeeMonthlyCharge = (commercialProduct: CommercialProduct, corporateShare: number) => {
  const monthlyCharge = Math.round(calculateMonthlyChargeWithCorporateShare(commercialProduct, corporateShare));
  return monthlyCharge > 0 && isEppProduct(commercialProduct) ? monthlyCharge : 0;
};

const isEppCatalog = (catalog: Catalog) => catalog.productType === Catalog.ProductTypeEnum.EPP_RECURRING;

const calculateEmployeeCharges = (cp: CommercialProduct, catalog: Catalog): CommercialProduct => {
  const cpNewPayments = { ...cp };
  if (!isEppCatalog(catalog!)) {
    cpNewPayments.oneTimeCharge = 0;
    cpNewPayments.monthlyRecurringCharge = 0;
  } else {
    cpNewPayments.oneTimeCharge = 0;
    cpNewPayments.monthlyRecurringCharge =
      catalog && catalog.corporateShare === undefined
        ? 0
        : calculateEmployeeMonthlyCharge(cp, valueOrZero(catalog?.corporateShare));
  }
  return cpNewPayments;
};

const shiftOneTimePaymentToLast = (commercialProducts: CommercialProduct[]) => {
  const sortedCps = [...commercialProducts];
  sortedCps.push(
    sortedCps.splice(
      sortedCps.findIndex(cp => isOneTimePayment(cp)),
      1
    )[0]
  );
  return sortedCps;
};

const sortDevices = (commercialProducts: CommercialProduct[]) => {
  const eppCps = commercialProducts
    .filter(cp => isEppProduct(cp))
    .sort((a, b) => (valueOrZero(a?.monthlyRecurringCharge) < valueOrZero(b?.monthlyRecurringCharge) ? -1 : 1));

  let nonEppCps = commercialProducts
    .filter(cp => !isEppProduct(cp))
    .sort((a, b) => (valueOrZero(a?.monthlyRecurringCharge) < valueOrZero(b?.monthlyRecurringCharge) ? -1 : 1));

  if (nonEppCps.length) {
    nonEppCps = shiftOneTimePaymentToLast(nonEppCps);
  }

  return [...eppCps, ...nonEppCps];
};

const sortAccessories = (commercialProducts: CommercialProduct[]) => {
  const sortedCps = commercialProducts.sort((a, b) =>
    valueOrZero(a?.monthlyRecurringCharge) < valueOrZero(b?.monthlyRecurringCharge) ? -1 : 1
  );

  return shiftOneTimePaymentToLast(sortedCps);
};

/*
 *  Epp solution is applied only to devices.
 *  A special rule to not show recurring prices for accessories when epp is allowed.
 * */
const filterAllowedEppAdminProducts = (
  cp: CommercialProduct,
  companyInfo: CompanyInfoResponse,
  category?: string | undefined
) => {
  if (!isDevice(cp)) {
    return true;
  }
  if (cp.oneTimeCharge && cp.oneTimeCharge > 0 && category !== OnlineModelCategory.ACCESSORIES) {
    return isOneTimeChargeAllowed(companyInfo);
  }
  if (
    cp.productSubType !== CommercialProductSubType.EPP_DEVICE &&
    cp.monthlyRecurringCharge &&
    cp.monthlyRecurringCharge > 0
  ) {
    if (category === OnlineModelCategory.ACCESSORIES) {
      return false;
    }
    return isRecurringChargesAllowed(companyInfo);
  }
  return true;
};

const processAddOns = (user: AuthenticatedUser | undefined, addOns?: AddOn[]) => {
  return addOns?.map(addOn => {
    const newAddOn = { ...addOn };
    if (isEmployeeUser(user)) {
      newAddOn.addOnMonthlyRecurringCharge = 0;
      newAddOn.addOnOneTimeCharge = 0;
    } else {
      newAddOn.addOnMonthlyRecurringCharge = valueOrZero(addOn.addOnMonthlyRecurringCharge);
      newAddOn.addOnOneTimeCharge = valueOrZero(addOn.addOnOneTimeCharge);
    }
    return newAddOn;
  });
};

const addVatToCharges = (cp: CommercialProduct): CommercialProduct => {
  const cpWithVat = { ...cp };
  valueOrZero(cp?.oneTimeCharge) > 0
    ? (cpWithVat.oneTimeCharge = Math.round(valueOrZero(cp?.oneTimeCharge) * VAT_MULTIPLIER))
    : (cpWithVat.monthlyRecurringCharge = Math.round(valueOrZero(cp.monthlyRecurringCharge) * VAT_MULTIPLIER));
  return cpWithVat;
};

/**
 * Employee pricing: apply the catalog-driven corporate-share adjustment. Prices already reflect
 * the customer's negotiated rates because the v3 private online model is fetched per auth state.
 */
const processEmployeeOffer = (offer: Offer, catalog: Catalog, onlineModelCategory?: string): Offer => ({
  ...offer,
  commercialProducts: offer.commercialProducts
    .filter(cp => checkCpEligibilityForEmployee(cp, catalog, onlineModelCategory))
    .filter(cp => cp.active)
    .map(cp => calculateEmployeeCharges(cp, catalog)),
});

/**
 * EPP admin pricing: filter to EPP-allowed products and sort. Prices already reflect the
 * customer's negotiated rates and applicable campaigns from the v3 private online model.
 */
const processEppAdminOffer = (offer: Offer, companyInfo: CompanyInfoResponse, onlineModelCategory?: string): Offer => {
  const filteredCps = offer.commercialProducts
    .filter(cp => filterAllowedEppAdminProducts(cp, companyInfo, onlineModelCategory))
    .filter(cp => cp.active);
  return {
    ...offer,
    commercialProducts:
      filteredCps.length === 0
        ? []
        : isDevice(filteredCps[0])
          ? sortDevices(filteredCps)
          : sortAccessories(filteredCps),
  };
};

/**
 * Standard (corporate/public) pricing: trust the prices already resolved on the online model.
 * The v3 bulk-prices API applies campaigns, customer-level pricing and frame agreements
 * server-side, so the client only filters and sorts for presentation.
 */
const processStandardOffer = (offer: Offer, user?: AuthenticatedUser, companyInfo?: CompanyInfoResponse): Offer => ({
  ...offer,
  commercialProducts: offer.commercialProducts
    .filter(cp => cp.active)
    .filter(cp => !isEppProduct(cp))
    .filter(cp => isCommercialProductPaymentAllowed(cp, user?.segmentPricingGroup, companyInfo?.eppSolution))
    .filter(cp => cp.payments || cp.oneTimeCharge || cp.monthlyRecurringCharge)
    .map(cp => (isEmployeeUser(user) ? addVatToCharges(cp) : cp))
    .sort((a, b) => (valueOrZero(a.payments) > valueOrZero(b.payments) ? -1 : 1)),
});

const processOffers = (
  onlineModel: OnlineModel,
  companyInfo?: CompanyInfoResponse,
  user?: AuthenticatedUser,
  catalog?: Catalog
): Offer[] =>
  onlineModel.offers
    .filter(offer => offer.active)
    .map(offer => {
      const normalizedOffer = {
        ...offer,
        commercialProducts: offer.commercialProducts.map(normalizeCommercialProductCharges),
      };

      if (isEmployeeUser(user) && catalog) {
        return processEmployeeOffer(normalizedOffer, catalog, onlineModel.category);
      }
      if (isEligibleEppAdmin(user, companyInfo)) {
        return processEppAdminOffer(normalizedOffer, companyInfo!, onlineModel.category);
      }
      return processStandardOffer(normalizedOffer, user, companyInfo);
    })
    .filter(offer => offer.commercialProducts.length !== 0);

export const processOnlineModel = (
  onlineModel: OnlineModel,
  companyInfo?: CompanyInfoResponse,
  user?: AuthenticatedUser,
  catalog?: Catalog
): OnlineModel => {
  const newModel = { ...onlineModel };
  newModel.offers = processOffers(onlineModel, companyInfo, user, catalog);
  newModel.addOns = processAddOns(user, onlineModel?.addOns?.map(normalizeAddOnCharges));
  return newModel;
};

export const useProcessOnlineModel = (
  onlineModel: OnlineModel,
  companyInfo?: CompanyInfoResponse,
  user?: AuthenticatedUser,
  catalog?: Catalog
): OnlineModel => {
  // TODO: leo 17.6.2022 - remove below `prettier-ignore` when below eslint disabling of react-hooks is removed
  // prettier-ignore
  return useMemo(
    () => processOnlineModel(onlineModel, companyInfo, user, catalog),
    [ /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps
      onlineModel,
      companyInfo?.eppSolution?.eppSolutionStatus,
      user?.userRole,
      catalog?.catalogCode,
    ]
  );
};

/** Addon handling functions **/

const formatEppCategory = (eppCategory: OfferEppCategory): EppCategory => {
  // @ts-ignore
  return EppCategory[eppCategory.trim().replace(/ /g, '_').toUpperCase()];
};

const isDamageInsuranceAddOnShown = (
  addOn: AddOn,
  eppCategories: EppCategory[] | undefined,
  offerEppCategory: OfferEppCategory | undefined
): boolean => {
  if (addOn.addOnGroup === DAMAGE_INSURANCE_ADDON_GROUP) {
    if (eppCategories && offerEppCategory) {
      return eppCategories.includes(formatEppCategory(offerEppCategory));
    }
    return false;
  }
  return true;
};

const isAddOnShown = (addOn: AddOn, addOnAssociations: AddOnAssociation[]): boolean => {
  for (const addOnAssociation of addOnAssociations || []) {
    if (addOn.addOnCode === addOnAssociation.addOnCode) {
      return true;
    }
  }
  return false;
};

const isAddOnCheckedByDefault = (addOn: AddOn): boolean => addOn.addOnGroup === DAMAGE_INSURANCE_ADDON_GROUP;

const getAddOnAssociationData = (addOnAssociations: AddOnAssociation[], addOnCode: string) => {
  const matchingAddOnAssociation = addOnAssociations.find(association => association.addOnCode === addOnCode);
  return {
    display: matchingAddOnAssociation!.display,
    addOnPurpose: matchingAddOnAssociation!.addOnPurpose,
    addOnAssociationId: matchingAddOnAssociation!.addOnAssociationId,
    addOnAssociationCode: matchingAddOnAssociation!.addOnAssociationCode,
  };
};
export const getAddOnsAndDefaultValues = (
  addOns: AddOn[],
  commercialProduct: CommercialProduct,
  eppCategories: EppCategory[] | undefined,
  isEmployee: boolean,
  productQuantity: number,
  offerEppCategory?: OfferEppCategory,
  user?: AuthenticatedUser
): ProductDetailsAddOnStateProperties[] => {
  const isSquareTradeAllowed = userHasEmptyOrSMEPriceGroup(user?.segmentPricingGroup);
  const validAddOns = addOns
    .filter(addOn => isDamageInsuranceAddOnShown(addOn, eppCategories, offerEppCategory))
    .filter(addOn => isAddOnShown(addOn, commercialProduct?.addOnAssociations ?? []))
    .filter(addOn => isSquareTradeAllowed || !isSquareTradeAddOn(addOn));
  return (
    validAddOns.map(addOn => {
      const isCheckedByDefault = isAddOnCheckedByDefault(addOn);
      return {
        addOn,
        isChecked: isCheckedByDefault,
        isDisabled: isEmployee && isCheckedByDefault,
        quantity: productQuantity,
        ...getAddOnAssociationData(commercialProduct.addOnAssociations!, addOn.addOnCode),
      };
    }) || []
  );
};

const calculateCommercialProductPayments = (cp: CommercialProduct, quantity = 1, payments: Price): Price => {
  if (isOneTimePayment(cp)) {
    payments.oneTimeCharge = cp.oneTimeCharge! * quantity;
    payments.payments = 1;
  } else {
    payments.monthlyRecurringCharge = cp.monthlyRecurringCharge! * quantity;
    payments.payments = cp.payments;
  }

  return payments;
};

const calculateAddOnPayments = (addOns: ProductDetailsAddOnStateProperties[], payments: Price): Price => {
  const addOnPayments = { ...payments };
  addOns
    .filter(addOn => addOn.isChecked)
    .forEach(addOn => {
      isOneTimePayment(undefined, addOn.addOn)
        ? (addOnPayments.oneTimeCharge =
            valueOrZero(addOnPayments.oneTimeCharge) + valueOrZero(addOn.addOn.addOnOneTimeCharge) * addOn.quantity)
        : (addOnPayments.monthlyRecurringCharge =
            valueOrZero(addOnPayments.monthlyRecurringCharge) +
            valueOrZero(addOn.addOn.addOnMonthlyRecurringCharge) * addOn.quantity);
    });
  return addOnPayments;
};

/**
 * Calculate VAT if applicable
 * VAT is shown to employee users only
 * @param charge
 * @param isVat
 */
export const calculateVatIfApplicable = (charge: number, isVat = false) =>
  isVat ? Math.round(charge * VAT_MULTIPLIER) : charge;

export const calculatePaymentsForSubscriptions = (offer: Offer, cpQuantity: number): Price => {
  const oneTimeCharge = offer.commercialProducts
    .flatMap(commercialProduct => (commercialProduct.oneTimeCharge || 0) * cpQuantity)
    .reduce((previous, current) => (current ? current : previous));
  const monthlyRecurringCharge = offer.commercialProducts
    .flatMap(commercialProduct => (commercialProduct.monthlyRecurringCharge || 0) * cpQuantity)
    .reduce((previous, current) => (current ? current : previous));
  return { monthlyRecurringCharge, oneTimeCharge };
};

export const calculatePaymentsForProducts = (
  cp: CommercialProduct,
  cpQuantity = 1,
  addOns?: ProductDetailsAddOnStateProperties[]
): Price => {
  let payments: Price = { monthlyRecurringCharge: 0, oneTimeCharge: 0 };
  if (cp) {
    payments = calculateCommercialProductPayments(cp, cpQuantity, payments);
  }
  if (addOns?.length) {
    payments = calculateAddOnPayments(addOns!, payments);
  }
  return payments;
};

export const checkIfVatIsRequired = (price: Price, includeVat?: boolean) => {
  if (includeVat === true) {
    return {
      monthlyRecurringCharge: Math.round(valueOrZero(price.monthlyRecurringCharge) * VAT_MULTIPLIER),
      oneTimeCharge: Math.round(valueOrZero(price.oneTimeCharge) * VAT_MULTIPLIER),
      payments: price.payments,
    };
  }
  return price;
};

export const calculatePayments = (
  cp: CommercialProduct,
  offer: Offer,
  cpQuantity = 1,
  addOns?: ProductDetailsAddOnStateProperties[],
  includeVat?: boolean
): Price => {
  const price =
    cp.productType === CommercialProductType.SALES_PRODUCT
      ? calculatePaymentsForSubscriptions(offer, cpQuantity)
      : calculatePaymentsForProducts(cp, cpQuantity, addOns);
  return checkIfVatIsRequired(price, includeVat);
};

const getSelectedAddOnBundles = (
  addOns: ProductDetailsAddOnStateProperties[],
  quantity: number
): SelectedAddOnBundle[] => {
  return addOns
    .filter(addOn => addOn.isChecked)
    .map(addOn => ({
      display: addOn.display,
      addOnPurpose: addOn.addOnPurpose,
      addOnAssociationCode: addOn.addOnAssociationCode,
      addOnAssociationId: addOn.addOnAssociationId,
      addOnCode: addOn.addOn.addOnCode,
      count: quantity,
      isMandatory: addOn.isDisabled,
    }));
};

export const onClickAddToCart = (
  props: ProductDetailsPropsComponentBottomRightSide,
  addOns: ProductDetailsAddOnStateProperties[],
  productPath: string,
  isAuthenticated: boolean
) => {
  const { addToCart, commercialProduct, isPublicSite, offer, onlineModel, productQuantity } = props;

  if (productQuantity < 1) {
    return undefined;
  }

  const selectedOffer = onlineModel.offers.find(of => of.offerCode === offer.offerCode)!;
  const selectedCommercialProduct = selectedOffer.commercialProducts.find(
    cp => cp.commercialProductCode === commercialProduct.commercialProductCode
  )!;
  const selectedAddOnBundles = getSelectedAddOnBundles(addOns, productQuantity);
  const cartItemContainer = {
    onlineModel,
    offer: selectedOffer,
    commercialProduct: selectedCommercialProduct,
    quantity: productQuantity,
    isPublicSite,
    selectedAddOnBundles,
    additionalFields: [],
    productPath,
  };

  if (isPublicSite) {
    addToShoppingBasketFromContainer(cartItemContainer, isAuthenticated);
    return undefined;
  } else {
    return addToCart(cartItemContainer);
  }
};

export const getOfferPictures = (pictures: PictureData[], offer: Offer): PictureData[] => {
  if (!offer.images?.length) {
    return pictures.filter(picture => picture.offerIds?.includes(offer.offerId));
  }

  return offer.images.flatMap(image => {
    const source = getImageUrl(image.trim());
    const picture = pictures.find(pic => pic.src === source && pic.offerIds?.includes(offer.offerId));
    return picture ? [picture] : [];
  });
};
export const getDefaultPaymentTerm = (onlineModelCategory?: string): number => {
  if ((onlineModelCategory as OnlineModelCategory) === OnlineModelCategory.ACCESSORIES) {
    return 1;
  }
  return 24;
};

// Prefer an EPP option as the default selection (matching the default payment term first, otherwise
// the first available EPP option) so EPP-eligible admins see EPP-specific pricing/add-ons by default.
export const selectDefaultCommercialProduct = (
  commercialProducts: CommercialProduct[],
  onlineModelCategory?: string
): CommercialProduct =>
  commercialProducts.find(
    product => isEppProduct(product) && product.payments === getDefaultPaymentTerm(onlineModelCategory)
  ) ||
  commercialProducts.find(product => isEppProduct(product)) ||
  commercialProducts.find(product => product.payments === getDefaultPaymentTerm(onlineModelCategory)) ||
  commercialProducts[0];

const employeeVAT = getVatAsString();
const corporateVAT = '0';

export const getEffectiveVAT = (isEmployee: boolean) => (isEmployee ? employeeVAT : corporateVAT);
